import { useMemo } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { FormattedMessage } from 'react-intl';
import useIntl from '../../hooks/useIntl';
import {
  checkAffordableProgramMismatch,
  getIsUnitAssignDisabled,
  normalizeContractNumber,
  checkShouldRenderAssignOption,
  checkApplicationMatchesPropertyClass,
  checkNaurAssignmentRestriction,
  checkPreferredFloorPlanRestriction,
  checkTransferDateOverlapsPriorOccupancy,
  sanitizeForCSV,
} from './utils';
import useSearchParams from './hooks.useSearchParams';
import { accessibilityTypeCell } from '../../utils/ada-helpers';
import { HUD_PRIMARY_SUBSIDY_TYPES } from '../../utils/affordablePrograms/HUD/hudMasterSubsidyTypes';
import { useMarketRateUnitsNaurWarnings } from '../../hooks/data-fetching/useNaurWarnings';

import messages from '../ManageUnitAvailability/messages';
import componentMessages from './messages';

const useRows = ({
  applicantToAssignUnit: {
    id: applicantToAssignUnitId,
    propertyClass: applicantPropertyClass,
    applicationStatus: applicantApplicationStatus,
    isCommercial: applicationIsCommercial,
    prospectInfo,
  },
  assignUnitProps: { handleAssignUnitClick, handleUnAssignUnitClick },
  hasManageUnitsUpdatePermission,
  unitQuoteMutation: { mutate: getUnitQuote },
  sortedAndFilteredResults,
  HUDTransferProps,
  propertyConfig,
  transferDate,
}) => {
  const {
    applicantId,
    prospectId,
    isTransfer,
    isCurrentUnitConventional,
    fromBuilding,
    isCurrentUnitLIHTC,
  } = useSearchParams();
  const {
    naur,
    unitTransfers,
    affordableWaitlistV2: waitlistRestrictionsEnabled = false,
  } = useFlags();

  const singleBuildingElection =
    propertyConfig?.singleBuildingElection ?? false;

  const intl = useIntl();

  // Extract all unit IDs from results
  const unitIds = useMemo(
    () =>
      sortedAndFilteredResults.map((result) => ({
        unitId: result.unitId,
        isFloorPlanAffordable: result.isFloorPlanAffordable,
      })),
    [sortedAndFilteredResults],
  );

  // Batch fetch NAUR data for all units
  const unitsNaurData = useMarketRateUnitsNaurWarnings(unitIds, naur);

  const { preferredFloorPlanId } = prospectInfo?.prospectPreferences ?? {};

  return useMemo(() => {
    return sortedAndFilteredResults.map((result) => {
      const {
        assignedApplicationId,
        isFloorPlanAffordable,
        isFloorPlanCommercial,
        building,
        fpPap,
        transferFpPap,
        originalUnit,
      } = result;

      // Get NAUR data for this unit
      const unitNaurData = unitsNaurData[result.unitId];

      const shouldRenderAssignOption = checkShouldRenderAssignOption({
        applicationId: applicantToAssignUnitId,
        assignedApplicationId,
      });

      const {
        currentUnitHudCode = '',
        hasMultipleHUDSubsidies = false,
        lastCertIsTermination = false,
        // ENG-6289
        currentUnitContractNumber = '',
        unitContractNumbers = {},
      } = HUDTransferProps ?? {};

      const isSelfAssigned = assignedApplicationId === applicantToAssignUnitId;

      const shouldRenderUnAssignOption =
        applicantToAssignUnitId &&
        assignedApplicationId &&
        applicantToAssignUnitId === assignedApplicationId;

      const isNotSameHUDTypeFloorplan =
        currentUnitHudCode !== (result?.floorPlanHudCode ?? '');

      /*
       * ENG-6289: both contract numbers arrive already resolved from the backend (explicit
       * AffordableUnitFunding link, else the property's primary HUD contract, else ''). Normalize
       * anyway — an empty OR whitespace-only value on either side means "unknown" and must never
       * block, and coercing to string keeps a numeric contract number from throwing.
       */
      const unitContractNumber = normalizeContractNumber(
        unitContractNumbers[result.unitId],
      );
      const currentContractNumber = normalizeContractNumber(
        currentUnitContractNumber,
      );
      const isDifferentHudContract =
        Boolean(currentContractNumber) &&
        Boolean(unitContractNumber) &&
        currentContractNumber !== unitContractNumber;

      // ENG-6289: the row is greyed by the program-set rule, which had no message of its own.
      const isAffordableProgramMismatch = checkAffordableProgramMismatch({
        fpPap,
        transferFpPap,
      });

      /*
       * ENG-32373: the ENG-4726 rule below greys the row with no message. Mirrored here verbatim
       * so the tooltip chain can name it — the rule itself is unchanged. Despite the
       * `isNotSameHUDTypeFloorplan` name it compares HUD SUBSIDY codes, not floor plans
       * (clarified in #eng 2026-09-16), so the message talks about subsidies.
       *
       * Deliberately NOT gated on `unitTransfers`: the block it explains is not gated either, so
       * gating the message would leave the row silently disabled whenever the flag is off — which
       * is the defect being fixed.
       */
      const isDifferentHudSubsidy =
        isTransfer &&
        isNotSameHUDTypeFloorplan &&
        hasMultipleHUDSubsidies &&
        !lastCertIsTermination;

      const unitHudSubsidyName = HUD_PRIMARY_SUBSIDY_TYPES.find(
        (subsidyType) => subsidyType.code === result?.floorPlanHudCode,
      )?.name;
      const isUnitAssignDisabled = getIsUnitAssignDisabled({
        assignedApplicationId,
        applicantToAssignUnitId,
        applicantApplicationStatus,
        applicantPropertyClass,
        isFloorPlanAffordable,
        isFloorPlanCommercial,
        isSelfAssigned,
        applicationIsCommercial,
        isTransfer,
        isCurrentUnitConventional,
        isNotSameHUDTypeFloorplan,
        hasMultipleHUDSubsidies,
        lastCertIsTermination,
        isDifferentHudContract,
        flags: { naur, unitTransfers },
        waitlistRestrictionsEnabled,
        singleBuildingElection,
        fromBuilding,
        unitBuilding: building,
        fpPap,
        transferFpPap,
        isCurrentUnitLIHTC,
        unitNaurData,
        preferredFloorPlanId,
        originalUnit,
        transferDate,
        moveOutDate: result.priorOccupancyMoveOutDate,
      });

      const isFloorPlanTypeMismatch = !checkApplicationMatchesPropertyClass({
        applicationPropertyClass: applicantPropertyClass?.name,
        isFloorPlanAffordable,
        isFloorPlanCommercial,
        applicationIsCommercial,
      });

      const isRestrictedBecauseOfNaur = checkNaurAssignmentRestriction({
        isFloorPlanAffordable,
        unitNaurData,
      });

      const overlapsPriorOccupancy = checkTransferDateOverlapsPriorOccupancy({
        isTransfer,
        transferDate,
        moveOutDate: result.priorOccupancyMoveOutDate,
      });

      const assignUnitTooltip = (() => {
        // Naur check
        if (naur && isRestrictedBecauseOfNaur) {
          return intl.formatMessage(
            componentMessages.naurAssignmentRestriction,
          );
        }

        // ENG-30316: the prior household still occupies the unit on the transfer date. Ranked
        // below NAUR (a hard compliance block) and above the suitability mismatches below it.
        if (overlapsPriorOccupancy) {
          return intl.formatMessage(
            componentMessages.unitOccupiedOnTransferDate,
          );
        }

        if (
          checkPreferredFloorPlanRestriction({
            restrictionsEnabled: waitlistRestrictionsEnabled,
            preferredFloorPlanId,
            unitFloorPlanId: originalUnit?.floorPlanId,
          })
        ) {
          return intl.formatMessage(
            componentMessages.floorPlanQualificationRestriction,
          );
        }
        // floor plan check
        if (isUnitAssignDisabled && isFloorPlanTypeMismatch) {
          return intl.formatMessage(
            componentMessages.floorPlanTypeMismatchTooltip,
          );
        }
        if (
          isCurrentUnitLIHTC &&
          isTransfer &&
          singleBuildingElection &&
          fromBuilding !== building
        ) {
          return intl.formatMessage(
            componentMessages.singleBuildingElectionTooltip,
          );
        }
        // ENG-6289: a hard affordable-compliance block, so it out-ranks the generic
        // application-status message. unitContractNumber is always non-empty here.
        if (isTransfer && unitTransfers && isDifferentHudContract) {
          return intl.formatMessage(
            componentMessages.hudContractMismatchTooltip,
            {
              contractNumber: unitContractNumber,
            },
          );
        }

        /*
         * ENG-6289: the pre-existing program-set block had no message at all. Sits below the
         * contract branch because a HUD -> HUD contract mismatch is the more specific diagnosis;
         * the two can never both apply, since a program-mismatched target is non-HUD and therefore
         * absent from unitContractNumbers. HUD -> conventional rows are caught earlier by the
         * floorPlanTypeMismatchTooltip branch.
         */
        if (isTransfer && unitTransfers && isAffordableProgramMismatch) {
          return intl.formatMessage(
            componentMessages.affordableProgramMismatchTooltip,
          );
        }

        /*
         * ENG-32373: the ENG-4726 HUD subsidy rule. Sits below the program-set branch — a target
         * on the wrong affordable program also trips this one, and "not the same program" is the
         * better diagnosis there. The generic variant covers a code with no entry in
         * HUD_PRIMARY_SUBSIDY_TYPES, so an unmapped code still explains itself.
         */
        if (isDifferentHudSubsidy) {
          return unitHudSubsidyName
            ? intl.formatMessage(componentMessages.hudSubsidyMismatchTooltip, {
                subsidyName: unitHudSubsidyName,
              })
            : intl.formatMessage(
                componentMessages.hudSubsidyMismatchGenericTooltip,
              );
        }

        if (
          isUnitAssignDisabled &&
          !['Approved', 'In Process', 'Converted to Resident'].includes(
            applicantApplicationStatus?.name,
          )
        ) {
          return intl.formatMessage(
            componentMessages.invalidApplicationStatusTooltip,
          );
        }
        return null;
      })();

      return {
        unit: {
          variant: 'link',
          value: result.unit,
          to: result.unitLink,
        },
        floorPlan: {
          variant: 'link',
          to: result.floorPlanLink,
          value: result.floorPlan,
        },
        building: {
          value: result.building,
        },
        bedsBaths: {
          value: result.bedsBaths,
        },
        sqFeet: {
          variant: 'number',
          value: result.sqFeet,
        },
        rentableSqFeet: {
          variant: 'number',
          value: result.rentableSqFeet,
        },
        status: {
          value: result.status,
        },
        leased: {
          value: result.leased ? 'Yes' : 'No',
        },
        resident: {
          variant: 'link',
          to: result.residentLink,
          value: result.resident,
        },
        marketRent: {
          variant: 'currency',
          value: result.marketRent,
        },
        netMaxRent: {
          variant: 'currency',
          value: result.netMaxRent,
        },
        priorLeasedRent: {
          variant: 'currency',
          value: result.priorLeasedRent,
        },
        currentLeasedRent: {
          variant: 'currency',
          value: result.currentLeasedRent,
        },
        moveOutDate: {
          variant: 'date',
          value: result.moveOutDate,
        },
        moveInReady: {
          variant: 'date',
          value: result.moveInReady,
        },
        moveInReadyStatus: {
          value: result.moveInReadyStatus,
        },
        notes: {
          variant: 'description',
          value: sanitizeForCSV(result.notes),
        },
        daysVacant: {
          variant: 'number',
          value: result.daysVacant,
        },
        quotingRent: {
          variant: 'currency',
          value: result.quotingRent,
        },
        otherUnitFeesAvailable: {
          variant: 'currency',
          value: result.otherUnitFeesAvailable,
        },
        applicant: {
          variant: 'link',
          to: result.applicantLink,
          value: result.applicant,
        },
        applicantStatus: {
          value: result.applicantStatus,
        },
        scheduledMoveInDate: {
          variant: 'date',
          value: result.scheduledMoveInDate,
        },
        amenities: {
          value: result.amenities,
        },
        ada: {
          value: result.ada,
        },
        accessibilityType: accessibilityTypeCell(result.accessibilityType),
        actions: {
          variant: 'menu',
          color: 'primary',
          closeOnClick: true,
          options: [
            {
              text: <FormattedMessage {...messages.quote} />,
              value: 'quote',
              onClick: () => {
                getUnitQuote({
                  unitId: result.unitId,
                  applicantId,
                  prospectId,
                });
              },
              disabled: isFloorPlanCommercial,
              tooltip: isFloorPlanCommercial
                ? 'Cannot provide quote on commercial unit'
                : null,
            },
            ...(hasManageUnitsUpdatePermission
              ? [
                  ...(shouldRenderUnAssignOption
                    ? [
                        {
                          text: <FormattedMessage {...messages.unassign} />,
                          value: 'unassign',
                          onClick: handleUnAssignUnitClick,
                        },
                      ]
                    : []),
                  ...(shouldRenderAssignOption
                    ? [
                        {
                          text: <FormattedMessage {...messages.assign} />,
                          value: 'assign',
                          disabled: isUnitAssignDisabled,
                          onClick: () => handleAssignUnitClick(result.unitId),
                          tooltip: assignUnitTooltip,
                        },
                      ]
                    : []),
                ]
              : []),
          ],
        },
      };
    });
  }, [
    sortedAndFilteredResults,
    applicantToAssignUnitId,
    HUDTransferProps,
    applicantApplicationStatus,
    applicantPropertyClass,
    applicationIsCommercial,
    isTransfer,
    isCurrentUnitConventional,
    isCurrentUnitLIHTC,
    naur,
    unitTransfers,
    unitsNaurData,
    singleBuildingElection,
    fromBuilding,
    intl,
    hasManageUnitsUpdatePermission,
    handleUnAssignUnitClick,
    getUnitQuote,
    applicantId,
    prospectId,
    handleAssignUnitClick,
    waitlistRestrictionsEnabled,
    preferredFloorPlanId,
    transferDate,
  ]);
};

export default useRows;
